import { useState, useEffect, useMemo, useRef } from "react";
import { useScrollIntoViewIfNotVisible } from "../../../hooks/useScrollIntoViewIfNotVisible";
import { useTranslation } from "@fmc-digital/i18n";
import type { MFAFactor } from "@fmc-digital/core-auth";
import {
  MFASelectorLayout,
  type MFASelectorLayoutFactor,
} from "@fmc-digital/ui";
import { formatFactorLabel } from "../../atoms/FactorLabel";
import type { MFASelectorContainerProps } from "./mfa-selector-container.types";

/**
 * Auth-shell MFA factor selection: maps {@link MFAFactor} from auth state into
 * {@link MFASelectorLayout} from `@fmc-digital/ui` (i18n, labels, initial selection).
 */
export function MFASelectorContainer({
  onNext,
  onCancel,
  initialFactorType,
  factors,
  isLoading = false,
  error = null,
  onErrorClear,
}: Readonly<MFASelectorContainerProps>) {
  const [selectedFactor, setSelectedFactor] =
    useState<MFASelectorLayoutFactor | null>(null);
  const { t } = useTranslation();
  const hasInitialized = useRef(false);

  const errorBannerRef = useRef<HTMLDivElement>(null);
  useScrollIntoViewIfNotVisible(errorBannerRef, !!error);

  const copy = useMemo(
    () => ({
      selectionTitle: t("auth.mfa.selectionTitle"),
      smsDisclaimer: t("auth.mfa.smsDisclaimer"),
      cancel: t("common.cancel"),
      next: t("common.next"),
      noMethodsTitle: t("auth.mfa.noMethodsTitle"),
      noMethodsDescription: t("auth.mfa.noMethodsDescription"),
      backToLogin: t("auth.mfa.backToLogin"),
    }),
    [t],
  );

  const formattedFactors = useMemo<MFASelectorLayoutFactor[]>(() => {
    return factors
      .map((factor): MFASelectorLayoutFactor => {
        const factorType: "email" | "sms" =
          (factor.factorType?.toLowerCase() ?? "") === "email"
            ? "email"
            : "sms";

        const profile = {
          email: factor.profile?.email,
          phoneNumber: factor.profile?.phoneNumber,
          credentialId: factor.profile?.credentialId,
        };

        const mfaFactor: MFAFactor = {
          ...factor,
          factorType,
          profile,
        };

        const label = formatFactorLabel(mfaFactor);

        return {
          id: factor.id,
          label,
          factorType,
          profile,
          link: factor.link,
        };
      })
      .sort((a, b) => {
        if (a.factorType === "sms" && b.factorType === "email") return -1;
        if (a.factorType === "email" && b.factorType === "sms") return 1;
        return 0;
      });
  }, [factors]);

  useEffect(() => {
    if (formattedFactors.length > 0) {
      if (initialFactorType !== undefined) {
        const matchingFactor = formattedFactors.find(
          (f) => f.factorType === initialFactorType,
        );
        if (matchingFactor) {
          setSelectedFactor(matchingFactor);
        } else {
          setSelectedFactor(formattedFactors[0]);
        }
        hasInitialized.current = true;
      } else if (!hasInitialized.current) {
        setSelectedFactor(formattedFactors[0]);
        hasInitialized.current = true;
      }
    }
  }, [initialFactorType, formattedFactors]);

  const handleSelectFactor = (factor: MFASelectorLayoutFactor) => {
    if (onErrorClear && factor.id !== selectedFactor?.id) {
      onErrorClear();
    }
    setSelectedFactor(factor);
  };

  const handleNext = () => {
    if (!selectedFactor) return;

    onNext({
      id: selectedFactor.id,
      factorType: selectedFactor.factorType,
      factorLabel: selectedFactor.label,
      profile: selectedFactor.profile,
      link: selectedFactor.link,
    });
  };

  return (
    <MFASelectorLayout
      copy={copy}
      factors={formattedFactors}
      selectedFactorId={selectedFactor?.id ?? ""}
      onSelectFactor={handleSelectFactor}
      onNext={handleNext}
      onCancel={onCancel}
      isLoading={isLoading}
      error={error}
      errorBannerRef={errorBannerRef}
    />
  );
}
